// src/pages/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  getDocs
} from 'firebase/firestore';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [matchedClaims, setMatchedClaims] = useState([]);
  const [approvedClaims, setApprovedClaims] = useState([]);
  const [lostItems, setLostItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [replyMap, setReplyMap] = useState({});

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== 'morinea@usiu.ac.ke') {
        alert('Access denied. Admins only.');
        navigate('/');
      }
    });

    const matchedQuery = query(collection(db, 'claims'), where('status', '==', 'matched'));
    const approvedQuery = query(collection(db, 'claims'), where('status', '==', 'approved'));
    const lostQuery = query(collection(db, 'lostItems'), where('reportType', '==', 'lost'));
    const faqsQuery = collection(db, 'userFAQs');

    const unsubscribeMatched = onSnapshot(matchedQuery, (snapshot) => {
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMatchedClaims(results);
    });

    const unsubscribeApproved = onSnapshot(approvedQuery, (snapshot) => {
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApprovedClaims(results);
    });

    getDocs(lostQuery).then(snapshot => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLostItems(items);
    });

    const unsubscribeFaqs = onSnapshot(faqsQuery, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFaqs(list);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeMatched();
      unsubscribeApproved();
      unsubscribeFaqs();
    };
  }, [navigate]);

  const handleAction = async (claimId, action, matchScore) => {
    if (action === 'approved' && matchScore < 100) {
      alert('Cannot approve. Match score is below 100.');
      return;
    }
    try {
      const claimRef = doc(db, 'claims', claimId);
      await updateDoc(claimRef, {
        status: action,
        updatedAt: new Date().toISOString()
      });
      alert(`Claim ${action}`);
    } catch (error) {
      alert('Error updating claim');
    }
  };

  const handleDelete = async (collectionName, id) => {
    const confirm = window.confirm('Are you sure you want to delete this record?');
    if (!confirm) return;
    await deleteDoc(doc(db, collectionName, id));
    if (collectionName === 'lostItems') setLostItems(prev => prev.filter(item => item.id !== id));
  };

  const handleClearAllItems = async () => {
    const confirm = window.confirm('Delete ALL lost items?');
    if (!confirm) return;
    const lostQuery = query(collection(db, 'lostItems'), where('reportType', '==', 'lost'));
    const snapshot = await getDocs(lostQuery);
    await Promise.all(snapshot.docs.map(docSnap => deleteDoc(doc(db, 'lostItems', docSnap.id))));
    setLostItems([]);
  };

  const handleClearAllClaims = async () => {
    const confirm = window.confirm('Are you sure you want to delete ALL claims?');
    if (!confirm) return;
    const claimsSnapshot = await getDocs(collection(db, 'claims'));
    await Promise.all(claimsSnapshot.docs.map(docSnap => deleteDoc(doc(db, 'claims', docSnap.id))));
    setMatchedClaims([]);
    setApprovedClaims([]);
    alert('All claims have been deleted.');
  };

  const handleReply = async (id) => {
    const answer = replyMap[id]?.trim();
    if (!answer) return alert('Type a reply.');
    await updateDoc(doc(db, 'userFAQs', id), { answer });
    setReplyMap(prev => ({ ...prev, [id]: '' }));
  };

  const formatDate = (value) => {
    try {
      if (!value) return 'N/A';
      if (typeof value === 'string') return new Date(value).toLocaleDateString();
      if (value.toDate) return value.toDate().toLocaleDateString();
      if (value.seconds) return new Date(value.seconds * 1000).toLocaleDateString();
      return new Date(value).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const buttonStyle = {
    backgroundColor: '#1B3B6F',
    color: 'white',
    padding: '0.5rem 1.2rem',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginRight: '1rem',
    marginBottom: '1rem'
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Admin Dashboard</h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <Link to="/item-matcher"><button style={buttonStyle}>Match Items (AI)</button></Link>
        <Link to="/system-report"><button style={buttonStyle}>System Summary</button></Link>
        <Link to="/add-security"><button style={buttonStyle}>Add Security Officer</button></Link>
        <button onClick={handleClearAllItems} style={{ ...buttonStyle, backgroundColor: '#cc0000' }}>Clear All Lost Items</button>
        <button onClick={handleClearAllClaims} style={{ ...buttonStyle, backgroundColor: '#800000' }}>Clear All Claims</button>
      </div>

      {/* Matched Claims Section */}
      
      {matchedClaims.map((item) => (
        <div key={item.id} style={{ backgroundColor: '#f9f9f9', padding: '1rem', marginBottom: '1rem' }}>
          <p><strong>Claimed:</strong> {item.claimedItem || item.itemName}</p>
          <p><strong>Match Score:</strong> {item.matchScore ?? 'N/A'}</p>
          <select
            defaultValue=""
            onChange={(e) => handleAction(item.id, e.target.value, Number(item.matchScore) || 0)}
            style={{ marginRight: '1rem' }}
          >
            <option value="" disabled>-- Action --</option>
            <option value="approved">Approve</option>
            <option value="rejected">Reject</option>
          </select>
          <button onClick={() => handleDelete('claims', item.id)} style={{ backgroundColor: 'red', color: 'white' }}>Delete</button>
        </div>
      ))}

      {/* Approved Claims Table */}
      <h3>Approved Claims</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '2rem',
          backgroundColor: '#eaffea',
          border: '1px solid #ccc'
        }}>
          <thead style={{ backgroundColor: '#1B3B6F', color: 'white' }}>
            <tr>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Claimed Item</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Status</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Approved Date</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {approvedClaims.map((item, i) => (
              <tr key={item.id} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f1fff1' }}>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{item.claimedItem || item.itemName}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>APPROVED</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{formatDate(item.updatedAt)}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
                  <button onClick={() => handleDelete('claims', item.id)} style={{ backgroundColor: 'red', color: 'white', border: 'none', padding: '0.4rem 1rem', borderRadius: '4px' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Lost Items Table */}
      <h3>📦 Lost Items</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: '#fff8f8',
          border: '1px solid #ccc',
          marginBottom: '2rem'
        }}>
          <thead style={{ backgroundColor: '#0a0942ff', color: 'white' }}>
            <tr>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Item Name</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Status</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Date Lost</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Description</th>
              <th style={{ padding: '0.8rem', border: '1px solid #ccc' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {lostItems.map((item, index) => (
              <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#fdf2f2' }}>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{item.itemName}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{item.itemStatus}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{formatDate(item.dateLost)}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>{item.description}</td>
                <td style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
                  <button
                    onClick={() => handleDelete('lostItems', item.id)}
                    style={{
                      backgroundColor: 'red',
                      color: 'white',
                      border: 'none',
                      padding: '0.4rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FAQs Section */}
      <h3>User Submitted FAQs</h3>
      {faqs.length === 0 ? <p>No questions yet.</p> : faqs.map((faq) => (
        <div key={faq.id} style={{ background: '#f4f4f4', padding: '1rem', marginBottom: '1rem' }}>
          <p><strong>Q:</strong> {faq.question}</p>
          <p><strong>A:</strong> {faq.answer || <em>No response</em>}</p>
          <textarea
            value={replyMap[faq.id] || ''}
            onChange={(e) => setReplyMap(prev => ({ ...prev, [faq.id]: e.target.value }))}
            placeholder="Type your reply"
            style={{ width: '100%', height: '60px', marginBottom: '0.5rem' }}
          />
          <button onClick={() => handleReply(faq.id)} style={buttonStyle}>Reply</button>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboard;
